import { FC } from "react";
import { Button } from "react-bootstrap";
import { Contract } from "@models/contract.model";

interface Props {
  contracts: readonly Contract[];
  selectedId: number | null;
  onSelect: (contractId: number) => void;
  /** Header label shown above the list. Defaults to "Contracts". */
  title?: string;
  /** Renders a label for the contract card (e.g., provider org name). Falls back to "Contract #{id}". */
  getLabel?: (contract: Contract) => string;
  /** Optional create handler — shows a "+ New" button in the header when provided. */
  onCreate?: () => void;
  /** Disables interaction (e.g., during a save). */
  isBusy?: boolean;
  emptyMessage?: string;
}

const ContractList: FC<Props> = ({
  contracts,
  selectedId,
  onSelect,
  title = "Contracts",
  getLabel,
  onCreate,
  isBusy,
  emptyMessage = "No contracts available",
}) => {
  return (
    <div className="contract-panel selector-panel">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <span className="panel-title">{title}</span>
          <span className="panel-count">{contracts.length}</span>
        </div>
        {onCreate && (
          <Button variant="primary" size="sm" onClick={onCreate} disabled={isBusy}>
            <i className="bi bi-plus-circle me-1" />
            New
          </Button>
        )}
      </div>

      {contracts.length === 0 ? (
        <div className="contract-empty-state" style={{ minHeight: 200 }}>
          <i className="bi bi-file-earmark-x empty-icon" />
          <div className="empty-title">{emptyMessage}</div>
        </div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {contracts.map((contract) => {
            const isSelected = contract.id === selectedId;
            const label =
              contract.name?.trim() || getLabel?.(contract) || `Contract #${contract.id}`;
            return (
              <button
                key={contract.id}
                type="button"
                disabled={isBusy}
                onClick={() => onSelect(contract.id)}
                className={`contract-list-item ${isSelected ? "is-selected" : ""}`}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className={`contract-list-item__title ${isSelected ? "selected" : ""}`}>
                      {label}
                    </div>
                    {contract.billing_method && (
                      <div className="contract-list-item__meta">{contract.billing_method}</div>
                    )}
                    {contract.po && (
                      <div className="contract-list-item__meta">PO: {contract.po}</div>
                    )}
                    {contract.contract_start_date && (
                      <div className="contract-list-item__date">
                        Started: {contract.contract_start_date}
                      </div>
                    )}
                  </div>
                  <div className="d-flex flex-column align-items-end gap-1">
                    <span
                      className={`contract-status-badge ${contract.is_active ? "active" : "inactive"}`}
                    >
                      {contract.is_active ? "Active" : "Inactive"}
                    </span>
                    {isSelected && <i className="bi bi-check-circle-fill contract-selected-check" />}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ContractList;
